import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSnackbar } from "notistack";
import {
  AppBar,
  Badge,
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Popover,
  Select,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import ActiveCallsList from "./ActiveCallsList";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CallReceivedOutlinedIcon from "@mui/icons-material/CallReceivedOutlined";
import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import ClearIcon from "@mui/icons-material/Clear";
import DialpadOutlinedIcon from "@mui/icons-material/DialpadOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import BackspaceIcon from "@mui/icons-material/Backspace";
import HearingOutlinedIcon from "@mui/icons-material/HearingOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import CTLNotification from "application/components/CTLNotification";
import DefaultView from "application/components/Map/DefaultView";
import GoogleMapView from "application/components/Map/GoogleMapView";
import FenceOutlinedIcon from "@mui/icons-material/FenceOutlined";
import GeofencePanel from "application/modules/Dispatcher/GeofencePanel";
import AlarmList from "application/components/Map/AlarmList";
import deviceAlertView from "application/components/Map/deviceAlertView";
import ActiveConference from "application/modules/activeConference";
import PPDRReport from "application/modules/configuration/Reports/ppdrReport";
import DispatcherTaskPanel from "application/modules/Dispatcher/DispatcherTaskPanel";
import ContactPanel from "application/modules/Dispatcher/ContactPanel";
import MapChatDispatcher from "application/components/Map/Chat/index";
import WebRTCPhone from "application/components/Map/WebRTCPhone";
import { VideoStreamArea, AudioStreamArea } from "./WaitingCallPanel";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import CryptoJS from "crypto-js";
import { QueryClient, QueryClientProvider } from "react-query";
import loggerFactory from "globals/logger/logger-factory";
import { application, telemetry } from "globals/endpoints";
import NotificationManager from "globals/websocket/WebSocket";
import {
  useMaterialUIController,
  setLayout,
  setClickToCall,
  setClickToVideoCall,
  setAmbientMode,
  setDarkMode,
  setSearchText,
  setDispatcherDepartment,
  setDispatcherEvent,
} from "context";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import {
  buildContactLookup,
  resolveContactIdentityByNumber,
} from "./contactLookup";

const queryClient = new QueryClient();
const DISPATCHER_OPEN_CHAT_EVENT = "dispatcher-open-chat";

function DispatcherMapView(props) {
  const [isGoogleMap, setIsGoogleMap] = useState(false);

  useEffect(() => {
    const mapURL = AuthenticationService.getMapUrl();
    const isGoogle = mapURL?.toLowerCase().includes("googlemap");
    setIsGoogleMap(isGoogle);
  }, []);

  const MapComponent = isGoogleMap ? GoogleMapView : DefaultView;

  return <MapComponent {...props} />;
}

const LEFT_PANEL_TABS = [
  {
    value: "contact",
    label: "Contact",
    icon: ContactsOutlinedIcon,
    component: ContactPanel,
  },
  {
    value: "ppdr-cdr",
    label: "CDR",
    icon: ReceiptLongOutlinedIcon,
    component: PPDRReport,
  },
  {
    value: "active-conference",
    label: "Active Conference",
    icon: GroupOutlinedIcon,
    component: ActiveConference,
  },
  {
    value: "geofence",
    label: "Geofence",
    icon: FenceOutlinedIcon,
    component: GeofencePanel,
  },
];

const CENTER_PANEL_TABS = [
  {
    value: "map-view",
    label: "Map View",
    icon: MapOutlinedIcon,
    component: DispatcherMapView,
  },
  {
    value: "chat",
    label: "Chat",
    icon: ChatOutlinedIcon,
    component: MapChatDispatcher,
  },
  {
    value: "task-management",
    label: "Task Management",
    icon: AssignmentTurnedInOutlinedIcon,
    component: DispatcherTaskPanel,
  },

];

const RIGHT_PANEL_TABS = [
  {
    value: "alarm-view",
    label: "SOS Alarm",
    icon: NotificationsActiveOutlinedIcon,
    component: AlarmList,
  },
  {
    value: "alert-view",
    label: "Device Alert",
    icon: WarningAmberOutlinedIcon,
    component: deviceAlertView,
  },
  {
    value: "active-calls",
    label: "Active Calls",
    icon: CallOutlinedIcon,
    component: ActiveCallsList,
  },
  // {
  //   value: "waiting-call",
  //   label: "Waiting Call",
  //   icon: PhoneInTalkOutlinedIcon,
  //   component: null,
  // },
];

const CHAT_NOTIFICATION_STORAGE_KEY = "chat-notifications-by-user";
const CHAT_NOTIFICATION_EVENT_NAME = "chat-notification-updated";
const NON_CHAT_SIGNAL_TOKENS = [
  "messageType=call",
  "messageType=others",
  "messageType=video",
  "messageType=audio",
  "messageType=PlayPublishVideo",
  "messageType=StopPublishVideo",
  "messageType=liveConferenceDisconnect",
];
const DISPATCHER_NOTIFICATION_TOPIC = "dispatcher-socket-notification";

function normalizeDispatcherNotificationType(type) {
  const normalizedType = String(type || "")
    .trim()
    .toUpperCase();

  if (normalizedType === "SOS") {
    return "SOS";
  }

  if (
    normalizedType === "ACTIVE_CALL" ||
    normalizedType === "ACTIVE CALL" ||
    normalizedType === "ACTIVECALL"
  ) {
    return "ACTIVE_CALL";
  }

  return "";
}

function getDispatcherBadgeKey(type) {
  if (type === "SOS") {
    return "sos";
  }

  if (type === "ACTIVE_CALL") {
    return "activeCalls";
  }

  return "";
}

function getDispatcherTabByType(type) {
  if (type === "SOS") {
    return "alarm-view";
  }

  if (type === "ACTIVE_CALL") {
    return "active-calls";
  }

  return "";
}

function getDispatcherNotificationStatus(type) {
  if (type === "SOS") {
    return "new sos recived";
  }

  if (type === "ACTIVE_CALL") {
    return "new active call live";
  }

  return "New Dispatcher Event";
}

function readChatNotificationsFromStorage() {
  try {
    const stored = localStorage.getItem(CHAT_NOTIFICATION_STORAGE_KEY);

    if (!stored) {
      return {};
    }

    const parsed = JSON.parse(stored);

    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
}

function writeChatNotificationsToStorage(notificationsByUser) {
  try {
    localStorage.setItem(
      CHAT_NOTIFICATION_STORAGE_KEY,
      JSON.stringify(notificationsByUser),
    );
    window.dispatchEvent(new Event(CHAT_NOTIFICATION_EVENT_NAME));
  } catch (error) {
    // Ignore storage errors and keep UI usable.
  }
}

function isIncomingChatTextMessage(message) {
  if (typeof message !== "string" || !message.trim()) {
    return false;
  }

  return !NON_CHAT_SIGNAL_TOKENS.some((token) => message.includes(token));
}

function parseIncomingChatMessage(message, fallbackSender = "") {
  if (typeof message !== "string") {
    return null;
  }

  const receiverMatch = message.match(/receiver=([^,]+)/);
  const contentMatch =
    message.match(/content=(.*?)(?=,\s*messageType=)/) ||
    message.match(/content=(.*)$/);
  const senderMatch = message.match(/sender=([^,]+)/);
  const createdAtMatch = message.match(/createdAt=([^,]+)/);
  const messageTypeMatch = message.match(/messageType=([^,]+)/);

  const receiver = receiverMatch?.[1]?.trim() || "";
  const sender = senderMatch?.[1]?.trim() || String(fallbackSender || "");

  if (!receiver || !sender) {
    return null;
  }

  return {
    receiver,
    sender,
    content: contentMatch?.[1]?.trim() || "",
    createdAt: createdAtMatch?.[1]?.trim() || "",
    messageType: messageTypeMatch?.[1]?.trim() || "text",
  };
}

function normalizeChatIdentifier(value) {
  const text = String(value || "").trim();

  if (!text) {
    return "";
  }

  const digitsOnly = text.replace(/\D/g, "");

  return digitsOnly.length >= 3 ? digitsOnly : text.toLowerCase();
}

function isSameChatIdentifier(first, second) {
  const normalizedFirst = normalizeChatIdentifier(first);
  const normalizedSecond = normalizeChatIdentifier(second);

  if (!normalizedFirst || !normalizedSecond) {
    return false;
  }

  return normalizedFirst === normalizedSecond;
}

function getUnreadChatCountFromStorage() {
  try {
    const storedNotifications = readChatNotificationsFromStorage();

    if (!storedNotifications) {
      return 0;
    }

    return Object.values(storedNotifications).reduce(
      (total, item) => total + Number(item?.count || 0),
      0,
    );
  } catch (error) {
    return 0;
  }
}

function normalizeDepartmentOptions(input) {
  if (!Array.isArray(input)) {
    return [];
  }

  const normalized = input
    .map((item, index) => {
      if (typeof item === "string") {
        return { label: item, value: item, raw: item };
      }

      if (!item || typeof item !== "object") {
        return null;
      }

      const label =
        item.department ||
        item.name ||
        item.label ||
        item.extension ||
        `Department ${index + 1}`;
      // Always identify a department by its code (`department`) so every
      // downstream comparison is code-based, not extension/name-based.
      const value =
        item.department || item.value || item.id || item.extension || label;

      return {
        label,
        value,
        color: typeof item === "object" && item?.color ? item.color : null,
        raw: item,
      };
    })
    .filter(Boolean);

  return normalized.filter(
    (option, index, array) =>
      array.findIndex((candidate) => candidate.value === option.value) ===
      index,
  );
}

export default function Dispatcher() {
  const theme = useTheme();
  const  department =  AuthenticationService.getDepartmentList() || [];
  const isCompactScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const [controller, dispatch] = useMaterialUIController();
  const { RTCEvent, darkMode, dispatcherEvent } = controller;
  const { enqueueSnackbar } = useSnackbar();
  const [leftPanelTab, setLeftPanelTab] = useState("contact");
  const [centerPanelTab, setCenterPanelTab] = useState("map-view");
  const [rightPanelTab, setRightPanelTab] = useState("alarm-view");
  const [searchValue, setSearchValueState] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [chatNotify, setChatNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "Chat",
    status: "",
  });
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactNotify, setContactNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "Contact",
    status: "",
  });
  const [dispatcherAlertCounts, setDispatcherAlertCounts] = useState({
    sos: 0,
    activeCalls: 0,
    geofence: 0,
  });
  const [pendingChatContact, setPendingChatContact] = useState(null);
  const [tryingAmbientTarget, setTryingAmbientTarget] = useState(null);
  const [primaryPanelWidth, setPrimaryPanelWidth] = useState(70);
  const [isResizing, setIsResizing] = useState(false);
  const [pageAttributes, setPageAttributes] = useState({});
  const [toolbarDialValue, setToolbarDialValue] = useState("");
  const [phoneToolbarState, setPhoneToolbarState] = useState({
    connectionState: "idle",
    pttStatus: "PTT Available",
    callActive: false,
    activeCallType: "",
    callTimer: "00:00:00",
    isVideoCall: false,
    waitingCalls: 0,
    isMuted: true,
    isVideoEnabled: false,
    canToggleVideo: false,
    isP2P: false,
    isAmbient: false,
    callList: [],
    remoteStreams: [],
    callStreamId: "",
  });
  const [dialPadAnchorEl, setDialPadAnchorEl] = useState(null);
  const [maximizedPanel, setMaximizedPanel] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [logoutPassword, setLogoutPassword] = useState("");
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [showLogoutPassword, setShowLogoutPassword] = useState(false);
  const [logoutNotify, setLogoutNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [webViewWaitingCalls, setWebViewWaitingCalls] = useState([]);
  const splitContainerRef = useRef(null);
  const panelCallbacksRef = useRef({});
  const phoneRef = useRef(null);
  const dialPadInputRef = useRef(null);
  const lastChatEventRef = useRef({ signature: "", handledAt: 0 });
  const processedChatMessagesRef = useRef(new Map());
  const processedDispatcherEventsRef = useRef(new Map());
  const callActiveRef = useRef(false);
  const phoneToolbarStateRef = useRef(phoneToolbarState);
  const rightPanelTabRef = useRef(rightPanelTab);
  const dialPadKeys = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["*", "0", "#"],
  ];

  const handleOpenDialPad = (event) => {
    setDialPadAnchorEl(event.currentTarget);
  };

  const handleCloseDialPad = () => {
    setDialPadAnchorEl(null);
  };

  const handleDialPadKey = (key) => {
    setToolbarDialValue((prev) => (prev + key).replace(/[^\d*#]/g, ""));
  };

  const handleDialPadBackspace = () => {
    setToolbarDialValue((prev) => prev.slice(0, -1));
  };

  const shouldPostWebViewCallEvent = useCallback((target) => {
    if (
      !(/wv|WebView|Edge\/\d+/i.test(navigator.userAgent) ||
        window.isWebView21 === true)
    ) {
      return false;
    }

    return true;
  }, []);

  const fetchContacts = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setContactsLoading(true);
    }

    try {
      const payload = {
        searchRequest:{

        },
        departmentList: department.map((dept) => dept.department),
      }
      const response = await application.post("uc/phoneBook/listByDepartment", payload);
      const allContacts = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      const departmentExtensions = new Set(
        department.map((dept) => String(dept.extension || "")).filter(Boolean)
      );

      const validContacts = allContacts.filter(
        (contact) =>
          (contact?.designation ||
            contact?.name ||
            contact?.offEXtn ||
            contact?.extension ||
            contact?.number) &&
          !departmentExtensions.has(String(contact?.offEXtn || contact?.extension || "")),
      );

      setContacts(validContacts);
    } catch (error) {
      loggerFactory.debug("Dispatcher Contact Lookup", error);
      setContactNotify({
        isOpen: true,
        message: "",
        type: "error",
        pagename: "Contact",
        status:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load contact list.",
      });
    } finally {
      if (!silent) {
        setContactsLoading(false);
      }
    }
  }, []);

  const syncUnreadChatCount = useCallback(() => {
    const unreadCount = getUnreadChatCountFromStorage();

    setChatUnreadCount((previousCount) =>
      previousCount === unreadCount ? previousCount : unreadCount,
    );
  }, []);

  useEffect(() => {
    setLayout(
      dispatch,
      AuthenticationService.isAuthenticated() ? "dashboard" : "page",
    );
  }, [dispatch]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    syncUnreadChatCount();

    const handleUnreadCountChange = (event) => {
      if (
        !event ||
        event.type === CHAT_NOTIFICATION_EVENT_NAME ||
        event.key === CHAT_NOTIFICATION_STORAGE_KEY
      ) {
        syncUnreadChatCount();
      }
    };

    const intervalId = window.setInterval(syncUnreadChatCount, 1200);

    window.addEventListener("storage", handleUnreadCountChange);
    window.addEventListener(
      CHAT_NOTIFICATION_EVENT_NAME,
      handleUnreadCountChange,
    );

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("storage", handleUnreadCountChange);
      window.removeEventListener(
        CHAT_NOTIFICATION_EVENT_NAME,
        handleUnreadCountChange,
      );
    };
  }, [syncUnreadChatCount]);

  useEffect(() => {
    const hasExistingClearHandler =
      typeof window.clearChatNotification === "function";
    const hasExistingGetHandler =
      typeof window.getChatNotifications === "function";

    if (hasExistingClearHandler && hasExistingGetHandler) {
      return undefined;
    }

    const clearChatNotification = (chatId) => {
      const key = String(chatId || "").trim();

      if (!key) {
        return;
      }

      const notificationsByUser = readChatNotificationsFromStorage();
      const comparableKey = normalizeChatIdentifier(key);
      const matchingKeys = Object.keys(notificationsByUser).filter(
        (candidateKey) =>
          candidateKey === key ||
          (comparableKey &&
            normalizeChatIdentifier(candidateKey) === comparableKey),
      );

      if (matchingKeys.length === 0) {
        return;
      }

      matchingKeys.forEach((matchedKey) => {
        delete notificationsByUser[matchedKey];
      });

      writeChatNotificationsToStorage(notificationsByUser);
      syncUnreadChatCount();
    };

    const getChatNotifications = () => {
      const notificationsByUser = readChatNotificationsFromStorage();

      return Object.entries(notificationsByUser)
        .map(([id, data]) => ({
          id,
          ...(data || {}),
        }))
        .sort(
          (first, second) =>
            Number(second?.count || 0) - Number(first?.count || 0),
        );
    };

    if (!hasExistingClearHandler) {
      window.clearChatNotification = clearChatNotification;
    }

    if (!hasExistingGetHandler) {
      window.getChatNotifications = getChatNotifications;
    }

    return () => {
      if (!hasExistingClearHandler) {
        delete window.clearChatNotification;
      }

      if (!hasExistingGetHandler) {
        delete window.getChatNotifications;
      }
    };
  }, [syncUnreadChatCount]);

  useEffect(() => {
    if (
      !dispatcherEvent ||
      dispatcherEvent.messageType !== "broadcast-text-message"
    ) {
      return;
    }

    const rawMessage = dispatcherEvent.message;

 
    try {
      if (tryingAmbientTarget) {
        const lower = String(rawMessage || "").toLowerCase();
        const isCallSignal = lower.includes("messagetype=call") || lower.includes("messageType=call") || /messageType=call/i.test(rawMessage);
        const hasAmbientBusy = /calltype\s*[:=]\s*ambient/.test(lower) && /callstate\s*[:=]\s*busy/.test(lower);

        const contentMatch = String(rawMessage || "").match(/content=([^,]+(?:,\s*[^,]+)*)/i);
        const contentText = contentMatch?.[1] || rawMessage;
        const contentLower = String(contentText || "").toLowerCase();
        const contentAmbientBusy = /calltype\s*[:=]\s*ambient/.test(contentLower) && /callstate\s*[:=]\s*busy/.test(contentLower);

        if (isCallSignal && (hasAmbientBusy || contentAmbientBusy)) {
          const signalInfo = parseIncomingChatMessage(rawMessage, "") || {};
          const busySender = signalInfo.sender;
           
           

          setTryingAmbientTarget(null);
          setAmbientMode(dispatch, false);
          setChatNotify({
            isOpen: true,
            message: "",
            type: "warning",
            pagename: "Dispatcher",
            status: `Unable to start ambient mode with - ${busySender} User is currently busy.`,
          });
          return;
        }
      }
    } catch (err) {
      console.warn("Error handling Ambient Busy in Dispatcher (early):", err);
    }

    if (!isIncomingChatTextMessage(rawMessage)) {
      return;
    }

    const signature = [
      dispatcherEvent.messageType,
      dispatcherEvent.message,
      dispatcherEvent.messageSentBy,
      dispatcherEvent.appName,
    ].join("|");
    const now = Date.now();

    if (
      signature &&
      lastChatEventRef.current.signature === signature &&
      now - lastChatEventRef.current.handledAt < 500
    ) {
      return;
    }

    lastChatEventRef.current = {
      signature,
      handledAt: now,
    };

    const parsedMessage = parseIncomingChatMessage(
      rawMessage,
      dispatcherEvent.userName || dispatcherEvent.messageSentBy,
    );

    if (!parsedMessage) {
      return;
    }

    const currentUser = String(
      AuthenticationService.getExtensionNumber() || "",
    );

    if (String(parsedMessage.sender) === currentUser) {
      return;
    }

    const eventMessageKey = [
      parsedMessage.sender,
      parsedMessage.receiver,
      parsedMessage.messageType,
      parsedMessage.createdAt || "",
      parsedMessage.content || "",
    ].join("|");
    const nowForEvent = Date.now();
    const lastProcessedAt =
      processedChatMessagesRef.current.get(eventMessageKey) || 0;

    if (nowForEvent - lastProcessedAt < 180000) {
      return;
    }

    processedChatMessagesRef.current.set(eventMessageKey, nowForEvent);

    if (processedChatMessagesRef.current.size > 600) {
      const pruneBefore = nowForEvent - 300000;
      for (const [key, timestamp] of processedChatMessagesRef.current) {
        if (timestamp < pruneBefore) {
          processedChatMessagesRef.current.delete(key);
        }
      }
    }

    const departmentList = AuthenticationService.getDepartmentList() || [];
    const matchedGroup = departmentList.find(
      (department) =>
        String(
          department?.extension ||
            department?.value ||
            department?.id ||
            department?.number ||
            "",
        ) === String(parsedMessage.receiver),
    );

    const notificationId = matchedGroup
      ? String(parsedMessage.receiver)
      : String(parsedMessage.sender);
    const notificationName = matchedGroup
      ? matchedGroup?.departmentDescription ||
        matchedGroup?.department ||
        matchedGroup?.name ||
        `Group ${parsedMessage.receiver}`
      : `User ${parsedMessage.sender}`;

    const isChatTabActive = centerPanelTab === "chat";
    const currentlyOpenedChatId = window.currentOpenedChat?.id;
    const isCurrentlyOpened = isSameChatIdentifier(
      currentlyOpenedChatId,
      notificationId,
    );

    if (isChatTabActive && isCurrentlyOpened) {
      return;
    }

    const notificationsByUser = readChatNotificationsFromStorage();
    const existingNotification = notificationsByUser[notificationId] || {
      count: 0,
      userName: notificationName,
      messages: [],
    };
    const existingMessages = Array.isArray(existingNotification?.messages)
      ? existingNotification.messages
      : [];
    const latestStoredMessage =
      existingMessages.length > 0
        ? existingMessages[existingMessages.length - 1]
        : null;
    const latestStoredTimestamp = new Date(
      latestStoredMessage?.timestamp || 0,
    ).getTime();
    const isAlreadyStoredMessage =
      String(latestStoredMessage?.createdAt || "") ===
        String(parsedMessage.createdAt || "") &&
      String(latestStoredMessage?.content || "") ===
        String(parsedMessage.content || "");
    const isLikelyImmediateDuplicate =
      !latestStoredMessage?.createdAt &&
      String(latestStoredMessage?.content || "") ===
        String(parsedMessage.content || "") &&
      Number.isFinite(latestStoredTimestamp) &&
      Date.now() - latestStoredTimestamp < 2000;

    if (isAlreadyStoredMessage || isLikelyImmediateDuplicate) {
      return;
    }

    notificationsByUser[notificationId] = {
      count: Number(existingNotification?.count || 0) + 1,
      userName: notificationName,
      messages: [
        ...existingMessages,
        {
          content: parsedMessage.content,
          createdAt: parsedMessage.createdAt || "",
          timestamp: new Date().toISOString(),
        },
      ].slice(-10),
    };

    writeChatNotificationsToStorage(notificationsByUser);
    syncUnreadChatCount();

    if (!isChatTabActive) {
      setChatNotify({
        isOpen: true,
        message: parsedMessage.content
          ? `From ${notificationName}: ${parsedMessage.content}`
          : `From ${notificationName}`,
        type: "info",
        pagename: "Chat",
        status: "New message received",
      });
    }
  }, [dispatcherEvent, centerPanelTab, syncUnreadChatCount]);

  useEffect(() => {
    const isDispatcherRole =
      AuthenticationService.allowMCXDispatcher() ||
      AuthenticationService.allowRailwayDispatcher();

    if (!isDispatcherRole) {
      return undefined;
    }

    const username = AuthenticationService.getUserName();
    const dispatcherCallbackId = `${DISPATCHER_NOTIFICATION_TOPIC}-${username}`;

    const handleDispatcherNotification = (message) => {
      let payload = null;

      try {
        payload = JSON.parse(message?.body || "{}");
      } catch (error) {
        loggerFactory.error(
          "Dispatcher Notification",
          "Unable to parse dispatcher event payload",
          error
        );
        return;
      }

      const notificationType = normalizeDispatcherNotificationType(payload?.type);

      if (!notificationType) {
        return;
      }

      const dedupeKey =
        message?.headers?.["message-id"] ||
        `${notificationType}:${payload?.timestamp || "no-ts"}:${payload?.message || ""}`;
      const now = Date.now();
      const previousHandledAt =
        processedDispatcherEventsRef.current.get(dedupeKey) || 0;

      if (now - previousHandledAt < 10000) {
        return;
      }

      processedDispatcherEventsRef.current.set(dedupeKey, now);

      if (processedDispatcherEventsRef.current.size > 200) {
        const pruneBefore = now - 300000;
        for (const [key, timestamp] of processedDispatcherEventsRef.current) {
          if (timestamp < pruneBefore) {
            processedDispatcherEventsRef.current.delete(key);
          }
        }
      }

      const badgeKey = getDispatcherBadgeKey(notificationType);
      const activeTab = getDispatcherTabByType(notificationType);
  
      if (badgeKey && rightPanelTabRef.current !== activeTab) {
        setDispatcherAlertCounts((previous) => ({
          ...previous,
          [badgeKey]: Number(previous?.[badgeKey] || 0) + 1,
        }));
      }

      setContactNotify({
        isOpen: true,
        status: payload?.message,
        type: "success",
        pagename: "Dispatcher Notification",
        message: "",
      });
    };

    NotificationManager.receiveMessageForDispatcher(
      handleDispatcherNotification,
      dispatcherCallbackId
    );

    return () => {
      NotificationManager.releaseDispatcher(dispatcherCallbackId);
    };
  }, []);

  const handleTelemetryEvent = useCallback(
    (message) => {
      if (!message?.body) return;
      try {
        const event = JSON.parse(message.body);
        setDispatcherEvent(dispatch, event);
      } catch (error) {
        loggerFactory.error("Dispatcher", "Unable to parse telemetry event", error);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const username = AuthenticationService.getUserName();

    if (!username) return undefined;

    const telemetryCallbackId = `dispatcher-telemetry-${username}`;

    NotificationManager.receiveMessageForTelemetry(
      `user-${username}`,
      handleTelemetryEvent,
      telemetryCallbackId
    );

    return () => {
      AuthenticationService.telemetryCallBacks.delete(telemetryCallbackId);
    };
  }, [handleTelemetryEvent]);

  // Geofence breach alert - USER_ZONE_ALERT_EVENT (112004), delivered on the
  // same per-user "user-{username}" topic subscribed to above.
  useEffect(() => {
    if (!dispatcherEvent || dispatcherEvent.eventId !== 112004) return;

    setDispatcherAlertCounts((previous) => ({
      ...previous,
      geofence: Number(previous?.geofence || 0) + 1,
    }));

    enqueueSnackbar(
      dispatcherEvent.eventMessage || "A user has exited a geofence",
      { variant: "info" },
    );
  }, [dispatcherEvent, enqueueSnackbar]);

  useEffect(() => {
    setSearchText(dispatch, searchValue);
  }, [dispatch, searchValue]);

  useEffect(() => {
    rightPanelTabRef.current = rightPanelTab;
  }, [rightPanelTab]);

  useEffect(() => {
    if (!isResizing || isCompactScreen) {
      return undefined;
    }

    const handleMouseMove = (event) => {
      const host = splitContainerRef.current;

      if (!host) {
        return;
      }

      const bounds = host.getBoundingClientRect();
      const nextWidth = ((event.clientX - bounds.left) / bounds.width) * 100;
      const constrainedWidth = Math.min(82, Math.max(45, nextWidth));

      setPrimaryPanelWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, isCompactScreen]);

  const isVideoCallTabLocked =
    phoneToolbarState.callActive && phoneToolbarState.isVideoCall;

  const effectiveWaitingCalls = shouldPostWebViewCallEvent("waiting-call")
    ? webViewWaitingCalls
    : phoneToolbarState.callList || [];
  const effectiveWaitingCallsCount = effectiveWaitingCalls.length;

  // Keep right panel on SOS Alarm during active video call.
  useEffect(() => {
    // Tab switching is unrestricted during calls.
  }, [isVideoCallTabLocked, rightPanelTab]);

  // Auto-switch to SOS Alarm when a call is active or incoming,
  // and back to SOS Alarm when no calls remain.
  useEffect(() => {
    if (phoneToolbarState.callActive) {
      setRightPanelTab("alarm-view");
    }
  }, [phoneToolbarState.callActive]);

  const leftTabConfig =
    LEFT_PANEL_TABS.find((tab) => tab.value === leftPanelTab) ||
    LEFT_PANEL_TABS[0];
  const LeftTabComponent = leftTabConfig.component;

  const centerTabConfig =
    CENTER_PANEL_TABS.find((tab) => tab.value === centerPanelTab) ||
    CENTER_PANEL_TABS[0];
  const CenterTabComponent = centerTabConfig.component;

  const rightTabConfig =
    RIGHT_PANEL_TABS.find((tab) => tab.value === rightPanelTab) ||
    RIGHT_PANEL_TABS[0];
  const RightTabComponent = rightTabConfig.component;

  const handlePanelLoad = useCallback((attributes = {}) => {
    setPageAttributes(attributes);
  }, []);

  const handleRegisterCallbacks = useCallback((callbacks = {}) => {
    panelCallbacksRef.current = callbacks;
  }, []);

  const departmentOptions = useMemo(() => {
    const panelDepartments = normalizeDepartmentOptions(pageAttributes?.value);
    const userDepartments = normalizeDepartmentOptions(
      AuthenticationService.getDepartmentList(),
    );

    return panelDepartments.length > 0 ? panelDepartments : userDepartments;
  }, [pageAttributes]);

  useEffect(() => {
    if (
      selectedDepartment &&
      !departmentOptions.some((option) => option.value === selectedDepartment)
    ) {
      setSelectedDepartment("");
    }
  }, [departmentOptions, selectedDepartment]);

  const handleDepartmentChange = (event) => {
    const value = event.target.value;
    setSelectedDepartment(value);

    const selectedOption = departmentOptions.find(
      (option) => option.value === value,
    );
    const selectedDepartmentMeta = selectedOption
      ? {
          value: selectedOption.value,
          label: selectedOption.label,
          department:
            selectedOption.raw?.department ||
            selectedOption.raw?.name ||
            selectedOption.label ||
            selectedOption.value,
          name:
            selectedOption.raw?.name ||
            selectedOption.raw?.department ||
            selectedOption.label,
          color:
            selectedOption.color ||
            (typeof selectedOption.raw === "object"
              ? selectedOption.raw?.color
              : null),
          number:
            selectedOption.raw?.extension ||
            selectedOption.raw?.number ||
            selectedOption.value,
          extension: selectedOption.raw?.extension || selectedOption.value,
          raw: selectedOption.raw,
        }
      : {
          value,
          label: value,
          department: value,
          name: value,
          color: null,
          number: value,
          extension: value,
          raw: null,
        };

    if (typeof panelCallbacksRef.current.onNMSSelect === "function") {
      panelCallbacksRef.current.onNMSSelect(value, selectedDepartmentMeta);
    }

    setDispatcherDepartment(dispatch, { value, meta: selectedDepartmentMeta });
  };

  const handleSearchChange = useCallback((event) => {
    setSearchValueState(event.target.value);
  }, []);

  const handleRightPanelTabChange = useCallback(
    (_event, value) => {
      if (value === "alarm-view") {
        setDispatcherAlertCounts((previous) => ({
          ...previous,
          sos: 0,
        }));
      }

      if (value === "active-calls") {
        setDispatcherAlertCounts((previous) => ({
          ...previous,
          activeCalls: 0,
        }));
      }

      setRightPanelTab(value);
    },
    [],
  );

  const handleToolbarDialChange = useCallback((event) => {
    setToolbarDialValue(event.target.value.replace(/[^\d*#]/g, ""));
  }, []);

  useEffect(() => {
    phoneToolbarStateRef.current = phoneToolbarState;
  }, [phoneToolbarState]);

  const handlePhoneStatusChange = useCallback((status = {}) => {
    setPhoneToolbarState((previous) => {
      const next = {
        ...previous,
        ...status,
      };

      if (
        previous.connectionState === next.connectionState &&
        previous.pttStatus === next.pttStatus &&
        previous.callActive === next.callActive &&
        previous.activeCallType === next.activeCallType &&
        previous.callTimer === next.callTimer &&
        previous.isVideoCall === next.isVideoCall &&
        previous.waitingCalls === next.waitingCalls &&
        previous.isMuted === next.isMuted &&
        previous.isVideoEnabled === next.isVideoEnabled &&
        previous.canToggleVideo === next.canToggleVideo &&
        previous.isP2P === next.isP2P &&
        previous.isAmbient === next.isAmbient &&
        previous.callList === next.callList &&
        previous.remoteStreams === next.remoteStreams &&
        previous.callStreamId === next.callStreamId &&
        previous.isBargeIn === next.isBargeIn &&
        previous.bargeInSentFrom === next.bargeInSentFrom &&
        previous.bargeInSentTo === next.bargeInSentTo
      ) {
        return previous;
      }

      callActiveRef.current = next.callActive;
      return next;
    });
  }, []);

  const triggerToolbarDial = useCallback(async (isVideo = false) => {
    const target = toolbarDialValue.replace(/[^\d*#]/g, "").trim();

    if (!target || !phoneRef.current) {
      return;
    }

    const didDial = isVideo
      ? await phoneRef.current.dialVideo(target)
      : await phoneRef.current.dialAudio(target);

    if (didDial) {
      setToolbarDialValue("");
    }
  }, [toolbarDialValue]);

  const triggerToolbarAnswer = useCallback(async () => {
    if (!phoneRef.current) {
      return;
    }

    await phoneRef.current.answerNextCall?.();
  }, []);

  const triggerToolbarReject = useCallback(async () => {
    if (!phoneRef.current) {
      return;
    }

    await phoneRef.current.rejectNextCall?.();
  }, []);

  const triggerToolbarDisconnect = useCallback(async () => {
    if (!phoneRef.current) {
      return;
    }

    await phoneRef.current.disconnectActiveCall?.();
  }, []);

  const triggerToolbarMicToggle = useCallback(async () => {
    if (!phoneRef.current) {
      return;
    }

    await phoneRef.current.toggleMic?.();
  }, []);

  const triggerToolbarVideoToggle = useCallback(async () => {
    if (!phoneRef.current) {
      return;
    }

    await phoneRef.current.toggleVideo?.();
  }, []);

  const handleLogout = () => {
    setLogoutPassword("");
    setShowLogoutPassword(false);
    setLogoutNotify({ isOpen: false, message: "", type: "" });
    setLogoutDialogOpen(true);
  };

  const isWebView21 = () => {
    return (
      /wv|WebView|Edge\/\d+/i.test(navigator.userAgent) ||
      window.isWebView21 === true
    );
  };

  useEffect(() => {
    if (!shouldPostWebViewCallEvent("agent/checkCallStatus")) {
      return undefined;
    }

    let isMounted = true;

    const syncWebViewWaitingCalls = async () => {
      try {
        const response = await application.post("agent/checkCallStatus");
        const data = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        if (!isMounted) {
          return;
        }

        const mappedCalls = data.map((item, index) => {
          const cidName = String(item?.cidName || "").trim();
          const cidNumber = String(item?.cidNumber || "").trim();
          const fallbackLabel = cidNumber || `Waiting Call ${index + 1}`;

          return {
            roomId:
              item?.uuid ||
              `${item?.queue || "queue"}-${cidNumber || "unknown"}-${index}`,
            uuid: item?.uuid || "",
            queue: item?.queue || "",
            cidName,
            cidNumber,
            state: item?.state || "",
            joinedEpoch: item?.joinedEpoch || null,
            sender: cidNumber,
            callerDisplayName: cidName || fallbackLabel,
            callerDisplayWithNumber:
              cidName && cidNumber
                ? `${cidName} (${cidNumber})`
                : cidName || cidNumber || fallbackLabel,
            isWebViewCall: true,
            isVideo: false,
          };
        });

        setWebViewWaitingCalls(mappedCalls);
      } catch (error) {
        if (isMounted) {
          setWebViewWaitingCalls([]);
        }
      }
    };

    syncWebViewWaitingCalls();
    const intervalId = window.setInterval(syncWebViewWaitingCalls, 1000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [shouldPostWebViewCallEvent]);

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
    setLogoutPassword("");
    setLogoutLoading(false);
    setShowLogoutPassword(false);
  };

  // Function to encrypt data with a random IV
  const encryptData = (data, secretKey) => {
    const iv = CryptoJS.lib.WordArray.random(16); // Generate a random IV
    const ciphertext = CryptoJS.AES.encrypt(
      data,
      CryptoJS.enc.Utf8.parse(secretKey),
      {
        iv: iv,
        mode: CryptoJS.mode.CFB,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    return iv.concat(ciphertext.ciphertext).toString(CryptoJS.enc.Base64);
  };

  const handleLogoutConfirm = async () => {
    if (!logoutPassword.trim()) {
      setLogoutNotify({
        isOpen: true,
        message: "Please enter your password.",
        type: "error",
      });
      return;
    }
    setLogoutLoading(true);
    try {
      const currentUser = AuthenticationService.getUserName() || "";
      const encryptedUsername = encryptData(currentUser, window.secretKey);
      const encryptedPassword = encryptData(logoutPassword, window.secretKey);

      await AuthenticationService.login(encryptedUsername, encryptedPassword);
      if (isWebView21()) {
        loggerFactory.info("click to logout event data send");
        window?.chrome?.webview?.postMessage(
          JSON.stringify({ eventType: "logout", data: "logout" }),
        );
        return;
      }
      setLogoutDialogOpen(false);
      AuthenticationService.logout();
      window.location.href = "/site/log-out";
    } catch (error) {
      setLogoutNotify({
        isOpen: true,
        message:
          error.response?.status === 400
            ? "Incorrect password. Please try again."
            : "Verification failed. Please try again.",
        type: "error",
      });
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleToggleDarkMode = useCallback(() => {
    setDarkMode(dispatch, !darkMode);
  }, [dispatch, darkMode]);

  const startResizingPanels = (event) => {
    if (isCompactScreen) {
      return;
    }

    event.preventDefault();
    setIsResizing(true);
  };

  const contactLookupByNumber = useMemo(
    () => buildContactLookup(contacts),
    [contacts],
  );

  const resolveContactIdentity = useCallback(
    (number) => resolveContactIdentityByNumber(contactLookupByNumber, number),
    [contactLookupByNumber],
  );

  useEffect(() => {
    window.resolveDispatcherContactIdentity = resolveContactIdentity;

    return () => {
      delete window.resolveDispatcherContactIdentity;
    };
  }, [resolveContactIdentity]);

  const resolveContactNumber = useCallback((contact = {}) => {
    const candidate =
      contact?.offEXtn ||
      contact?.extension ||
      contact?.number ||
      contact?.phone ||
      "";

    return String(candidate || "").trim();
  }, []);

  const handleContactAction = useCallback(
    (actionType, contact = {}) => {
      const target = resolveContactNumber(contact);

      if (!target) {
        setChatNotify({
          isOpen: true,
          message: "",
          type: "warning",
          pagename: "Contact",
          status: "Selected contact has no valid extension number.",
        });
        return;
      }

      if (
        actionType === "call" ||
        actionType === "video-call" ||
        actionType === "ambient-mode"
      ) {
        if (tryingAmbientTarget) {
          setContactNotify({
            isOpen: true,
            message: "",
            type: "warning",
            pagename: "Contact",
            status:
              "Ambient mode request is in progress. Please cancel or wait before starting a new call.",
          });
          return;
        }

        if (callActiveRef.current) {
          setContactNotify({
            isOpen: true,
            message: "",
            type: "warning",
            pagename: "Contact",
            status: "A call is already in progress. Please disconnect before starting a new one.",
          });
          return;
        }
      }

      if (actionType === "answer") {
        const callQueue = phoneToolbarStateRef.current.callList || [];
        const matchingCall = callQueue.find(
          (c) =>
            String(c.sender || "") === String(target) ||
            String(c.streamId || "") === String(target),
        );
        if (matchingCall && phoneRef.current?.joinCall) {
          phoneRef.current.joinCall(matchingCall);
        } else {
          triggerToolbarAnswer();
        }
        return;
      }

      if (actionType === "disconnect") {
        const callQueue = phoneToolbarStateRef.current.callList || [];
        const matchingCall = callQueue.find(
          (c) =>
            String(c.sender || "") === String(target) ||
            String(c.streamId || "") === String(target),
        );
        if (matchingCall && phoneRef.current?.rejectCall) {
          phoneRef.current.rejectCall(matchingCall);
        } else {
          triggerToolbarDisconnect();
        }
        return;
      }

      if (actionType === "call") {
        setClickToCall(dispatch, target);
        return;
      }

      if (actionType === "video-call") {
        setClickToVideoCall(dispatch, target);
        return;
      }

      if (actionType === "ambient-mode") {
        const payload = {
          kind: "D",
          sender: AuthenticationService.getExtensionNumber(),
          target,
          content: "callType: Ambient; callState: RequestAmbient;",
          messageType: "call",
        };

        telemetry.post("chat/message", payload).catch((error) => {
          loggerFactory.debug("Dispatcher Ambient Mode", error);
          setChatNotify({
            isOpen: true,
            message: "",
            type: "error",
            pagename: "Contact",
            status: "Unable to send ambient mode request.",
          });
        });

        setAmbientMode(dispatch, true);
        const ambientIdentity = resolveContactIdentity(target);
        setTryingAmbientTarget({
          label: ambientIdentity.designation || ambientIdentity.name || target,
          number: target,
        });
        setChatNotify({
          isOpen: true,
          message: "",
          type: "info",
          pagename: "Contact",
          status: `Please wait... Ambient mode request sent to ${target}`,
        });
        return;
      }

      if (actionType === "message") {
        const targetIdentity = resolveContactIdentity(target);
        const resolvedName =
          targetIdentity.designation || targetIdentity.name || String(target);

        const chatTarget = {
          offEXtn: target,
          extension: target,
          designation: resolvedName,
          name:
            targetIdentity.name || targetIdentity.designation || resolvedName,
        };

        if (centerPanelTab === "chat") {
          window.dispatchEvent(
            new CustomEvent(DISPATCHER_OPEN_CHAT_EVENT, {
              detail: chatTarget,
            }),
          );
          return;
        }

        setPendingChatContact(chatTarget);
        setCenterPanelTab("chat");
      }
    },
    [
      centerPanelTab,
      dispatch,
      tryingAmbientTarget,
      resolveContactIdentity,
      resolveContactNumber,
    ],
  );

  useEffect(() => {
    if (centerPanelTab !== "chat" || !pendingChatContact) {
      return;
    }

    const capturedContact = pendingChatContact;
    window.pendingDispatcherChatTarget = capturedContact;

    const timeoutId = window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent(DISPATCHER_OPEN_CHAT_EVENT, {
          detail: capturedContact,
        }),
      );
      window.pendingDispatcherChatTarget = null;
      setPendingChatContact(null);
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [centerPanelTab, pendingChatContact]);

  useEffect(() => {
    if (phoneToolbarState.callActive) {
      setTryingAmbientTarget(null);
    }
  }, [phoneToolbarState.callActive]);

  const sharedPanelProps = {
    onLoad: handlePanelLoad,
    pageId: "dispatcher",
    searchText: searchValue,
    registerCallBacks: handleRegisterCallbacks,
    pageName: "Dispatcher",
    isRightPanelExpanded: maximizedPanel === "right",
    isLeftPanelExpanded: maximizedPanel === "left",
    isCompactScreen,
    onContactAction: handleContactAction,
    isPhoneCallActive: phoneToolbarState.callActive,
    sosCallList: phoneToolbarState.callList || [],
    contacts,
    contactsLoading,
    onRefreshContacts: fetchContacts,
    resolveContactIdentityByNumber: resolveContactIdentity,
  };

  const dispatcherTitle = `Dispatcher${
    AuthenticationService.getUserName()
      ? ` - ${AuthenticationService.getUserName()}`
      : ""
  }`;

  const { toolbarConnectionLabel, toolbarConnectionColor, toolbarCallStatusColor } = useMemo(() => {
    const state = phoneToolbarState.connectionState;
    return {
      toolbarConnectionLabel:
        state === "connected" ? "Connected" :
        state === "connecting" ? "Connecting" :
        state === "disconnected" ? "Disconnected" : "",
      toolbarConnectionColor:
        state === "connected" ? theme.palette.success.main :
        state === "connecting" ? theme.palette.warning.main :
        state === "disconnected" ? theme.palette.error.main :
        theme.palette.info.main,
      toolbarCallStatusColor: phoneToolbarState.callActive
        ? theme.palette.text.primary
        : theme.palette.info.main,
    };
  }, [
    phoneToolbarState.connectionState,
    phoneToolbarState.callActive,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    theme.palette.text.primary,
  ]);

  const decoratedWaitingCalls = useMemo(
    () =>
      (effectiveWaitingCalls || []).map((call) => {
        if (call?.isWebViewCall) {
          return {
            ...call,
            callerDisplayName:
              call?.callerDisplayName || call?.cidName || call?.cidNumber || "",
            callerDisplayWithNumber:
              call?.callerDisplayWithNumber ||
              (call?.cidName && call?.cidNumber
                ? `${call.cidName} (${call.cidNumber})`
                : call?.cidName || call?.cidNumber || "Unknown"),
          };
        }

        const callerValue = call?.sender || call?.streamId || "";
        const callerIdentity = resolveContactIdentity(callerValue);

        return {
          ...call,
          callerDisplayName: callerIdentity.displayName,
          callerDisplayWithNumber: callerIdentity.displayWithNumber,
        };
      }),
    [effectiveWaitingCalls, resolveContactIdentity],
  );

  const {
    nextWaitingCall,
    incomingCallerLabel,
    incomingTypeLabel,
    incomingChipIcon,
    incomingChipLabel,
    incomingChipColorKey,
  } = useMemo(() => {
    const next = decoratedWaitingCalls[0] || null;
    const callerLabel = next?.callerDisplayWithNumber || "";
    const typeLabel = next
      ? next?.isSOS
        ? "SOS Call"
        : next?.isAmbient || next?.type === "Ambient"
          ? "Ambient Call"
          : `${next.isVideo ? "Video" : "Audio"} Call`
      : "";
    const chipIcon = next?.isSOS ? (
      <NotificationsActiveOutlinedIcon sx={{ fontSize: 16 }} />
    ) : next?.isAmbient || next?.type === "Ambient" ? (
      <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />
    ) : null;
    const chipLabel = next ? (callerLabel ? `${callerLabel}` : "Incoming Call") : "";
    const chipColorKey = next?.isSOS
      ? "error"
      : next?.isAmbient || next?.type === "Ambient"
        ? "warning"
        : "success";
    return {
      nextWaitingCall: next,
      incomingCallerLabel: callerLabel,
      incomingTypeLabel: typeLabel,
      incomingChipIcon: chipIcon,
      incomingChipLabel: chipLabel,
      incomingChipColorKey: chipColorKey,
    };
  }, [decoratedWaitingCalls]);

  const {
    activeCallLabel,
    activeCallTypeNormalized,
    isSOSCallActive,
    isAmbientCallActive,
    isBargeInCallActive,
    bargeInLabel,
    activeCallChipLabel,
    activeCallChipIcon,
    activeCallChipColorKey,
    isGroupCallActive,
  } = useMemo(() => {
    const identity = resolveContactIdentity(phoneToolbarState.callStreamId || "");
    const callLabel = identity.designation || identity.name || identity.number || "";
    const typeNorm = String(phoneToolbarState.activeCallType || "").toLowerCase();
    const isSOS = phoneToolbarState.callActive && typeNorm === "sos";
    const isAmbient = phoneToolbarState.callActive && typeNorm === "ambient";
    const isBargeIn = phoneToolbarState.callActive && phoneToolbarState.isBargeIn;

    let bInLabel = "";
    if (isBargeIn) {
      const fromName =
        resolveContactIdentity(phoneToolbarState.bargeInSentFrom)?.designation ||
        resolveContactIdentity(phoneToolbarState.bargeInSentFrom)?.name ||
        phoneToolbarState.bargeInSentFrom ||
        "Unknown";
      const toName =
        resolveContactIdentity(phoneToolbarState.bargeInSentTo)?.designation ||
        resolveContactIdentity(phoneToolbarState.bargeInSentTo)?.name ||
        phoneToolbarState.bargeInSentTo ||
        "Unknown";
      bInLabel = `${fromName} → ${toName}`;
    }

    const chipLabel = isSOS
      ? callLabel ? `${callLabel} - SOS Call` : "SOS Call"
      : isAmbient
        ? callLabel ? `${callLabel} - Ambient Call` : "Ambient Call"
        : isBargeIn
          ? `${bInLabel} - Barge In`
          : callLabel;

    const chipIcon = isSOS ? (
      <NotificationsActiveOutlinedIcon
        sx={{ fontSize: 16, mr: 0.25, color: (t) => `${t.palette.error.main} !important` }}
      />
    ) : isAmbient ? (
      <WarningAmberOutlinedIcon
        sx={{ fontSize: 16, mr: 0.25, color: (t) => `${t.palette.warning.main} !important` }}
      />
    ) : isBargeIn ? (
      <HearingOutlinedIcon
        sx={{ fontSize: 16, mr: 0.25, color: (t) => `${t.palette.info.main} !important` }}
      />
    ) : null;

    const chipColorKey = isSOS ? "error" : isAmbient ? "warning" : "info";
    const isGroup = phoneToolbarState.callActive && phoneToolbarState.activeCallType === "Group";

    return {
      activeCallLabel: callLabel,
      activeCallTypeNormalized: typeNorm,
      isSOSCallActive: isSOS,
      isAmbientCallActive: isAmbient,
      isBargeInCallActive: isBargeIn,
      bargeInLabel: bInLabel,
      activeCallChipLabel: chipLabel,
      activeCallChipIcon: chipIcon,
      activeCallChipColorKey: chipColorKey,
      isGroupCallActive: isGroup,
    };
  }, [
    phoneToolbarState.callActive,
    phoneToolbarState.callStreamId,
    phoneToolbarState.activeCallType,
    phoneToolbarState.isBargeIn,
    phoneToolbarState.bargeInSentFrom,
    phoneToolbarState.bargeInSentTo,
    resolveContactIdentity,
  ]);

  const resolvedPttStatus = useMemo(() => {
    const statusValue = String(phoneToolbarState.pttStatus || "PTT Available");
    const pttPrefix = "PTT Granted - ";

    if (!statusValue.startsWith(pttPrefix)) {
      return statusValue;
    }

    const floorUser = statusValue.slice(pttPrefix.length).trim();

    if (!floorUser || floorUser.toLowerCase() === "you") {
      return `${pttPrefix}You`;
    }

    const floorIdentity = resolveContactIdentity(floorUser);

    return `${pttPrefix}${floorIdentity.displayWithNumber}`;
  }, [phoneToolbarState.pttStatus, resolveContactIdentity]);

  const toolbarCallStatus = useMemo(() => {
    if (phoneToolbarState.callActive) {
      if (isGroupCallActive) return resolvedPttStatus;
      const timer = phoneToolbarState.callTimer;
      if (isSOSCallActive || isAmbientCallActive || isBargeInCallActive) {
        return timer !== "00:00:00" ? timer : "";
      }
      return timer;
    }
    return nextWaitingCall ? incomingTypeLabel || "Incoming Call" : "No Call Active";
  }, [
    phoneToolbarState.callActive,
    phoneToolbarState.callTimer,
    isGroupCallActive,
    isSOSCallActive,
    isAmbientCallActive,
    isBargeInCallActive,
    resolvedPttStatus,
    nextWaitingCall,
    incomingTypeLabel,
  ]);

  const showToolbarDuration =
    phoneToolbarState.callActive && phoneToolbarState.callTimer !== "00:00:00";

  const {
    hasWaitingCalls,
    isPrivateAudioCallActive,
    showTryingAmbient,
    isToolbarCallActionDisabled,
    isToolbarAnswerDisabled,
    isToolbarRejectDisabled,
    isToolbarDisconnectDisabled,
    showDialButtons,
    showClearButton,
    showAnswerButton,
    showRejectButton,
    showMicButton,
  } = useMemo(() => {
    const waitingCalls = effectiveWaitingCallsCount > 0;
    const tryingAmbient = Boolean(tryingAmbientTarget) && !phoneToolbarState.callActive;
    const dialVisible = !phoneToolbarState.callActive && !waitingCalls && !tryingAmbient;
    return {
      hasWaitingCalls: waitingCalls,
      isPrivateAudioCallActive:
        phoneToolbarState.callActive &&
        String(phoneToolbarState.activeCallType || "").toLowerCase() === "private" &&
        !phoneToolbarState.isVideoCall,
      showTryingAmbient: tryingAmbient,
      isToolbarCallActionDisabled: !toolbarDialValue.trim() || phoneToolbarState.callActive || tryingAmbient,
      isToolbarAnswerDisabled: !waitingCalls || phoneToolbarState.callActive,
      isToolbarRejectDisabled: !waitingCalls || phoneToolbarState.callActive,
      isToolbarDisconnectDisabled: !phoneToolbarState.callActive,
      showDialButtons: dialVisible,
      showClearButton: dialVisible && !!toolbarDialValue,
      showAnswerButton: waitingCalls && !phoneToolbarState.callActive,
      showRejectButton: waitingCalls && !phoneToolbarState.callActive,
      showMicButton: phoneToolbarState.callActive && !phoneToolbarState.isAmbient && !phoneToolbarState.isBargeIn,
    };
  }, [
    effectiveWaitingCallsCount,
    tryingAmbientTarget,
    phoneToolbarState.callActive,
    phoneToolbarState.activeCallType,
    phoneToolbarState.isVideoCall,
    phoneToolbarState.isAmbient,
    phoneToolbarState.isBargeIn,
    toolbarDialValue,
  ]);
  const cancelTryingAmbient = useCallback(() => {
    const target = String(tryingAmbientTarget?.number || "").trim();

    if (target) {
      const payload = {
        kind: "D",
        sender: AuthenticationService.getExtensionNumber(),
        target,
        content: "callType: Ambient; callState: Disconnected;",
        messageType: "call",
      };

      telemetry.post("chat/message", payload).catch((error) => {
        loggerFactory.debug("Dispatcher Ambient Cancel", error);
      });
    }

    setTryingAmbientTarget(null);
    setAmbientMode(dispatch, false);
  }, [tryingAmbientTarget, dispatch]);

  const showVideoToggleButton = useMemo(
    () => phoneToolbarState.callActive && phoneToolbarState.canToggleVideo,
    [phoneToolbarState.callActive, phoneToolbarState.canToggleVideo],
  );

  const dialTargetIdentity = useMemo(
    () => resolveContactIdentity(toolbarDialValue),
    [resolveContactIdentity, toolbarDialValue],
  );

  const showDialTargetIdentity =
    showDialButtons && Boolean(toolbarDialValue.trim());

  const disconnectTooltip = useMemo(
    () =>
      phoneToolbarState.callActive && !phoneToolbarState.isP2P && !phoneToolbarState.isAmbient
        ? "Leave Call"
        : "Disconnect Call",
    [phoneToolbarState.callActive, phoneToolbarState.isP2P, phoneToolbarState.isAmbient],
  );

  const showAnnouncementPanel = RTCEvent?.type === "announcement";
  const isRightMaximizeDisabled = isVideoCallTabLocked;

  const toggleMaximize = useCallback((panel) => {
    setMaximizedPanel((prev) => (prev === panel ? null : panel));
  }, []);

  useEffect(() => {
    if (isRightMaximizeDisabled && maximizedPanel === "right") {
      setMaximizedPanel(null);
    }
  }, [isRightMaximizeDisabled, maximizedPanel]);

  const getGridColumns = () => {
    if (!maximizedPanel) return { xs: "1fr", lg: "25% 1fr 25%" };
    if (maximizedPanel === "left") return { xs: "1fr", lg: "1fr 0fr 25%" };
    if (maximizedPanel === "center") return { xs: "1fr", lg: "0fr 1fr 25%" };
    if (maximizedPanel === "right") return { xs: "1fr", lg: "25% 0fr 1fr" };
    return { xs: "1fr", lg: "25% 1fr 25%" };
  };

  const isPanelVisible = (panel) => {
    if (!maximizedPanel) return true;
    if (maximizedPanel === "left") return panel !== "center";
    if (maximizedPanel === "right") return panel !== "center";
    if (maximizedPanel === "center") return panel !== "left";
    return true;
  };

  const collapsedCardSx = {
    minHeight: 0,
    minWidth: 0,
    overflow: "hidden",
    visibility: "hidden",
    pointerEvents: "none",
    border: "none",
    p: 0,
    m: 0,
  };

  const panelTabSx = {
    minHeight: 38,
    borderTop: (paletteTheme) =>
      `1px solid ${alpha(paletteTheme.palette.info.main, 0.45)}`,
    borderBottom: (paletteTheme) =>`1px solid ${alpha(paletteTheme.palette.info.main, 0.45)}`,
    "& .MuiTabs-indicator": { display: "none" },
    "& .MuiTab-root": {
      minHeight: 38,
      minWidth: 0,
      textTransform: "none",
      color: "text.secondary",
      fontWeight: 500,
      fontSize: theme.typography.pxToRem(11),
      borderRadius: 1,
      overflow: "visible",
      // mx: 0.25,
      // my: 0.25,
      // px: { xs: 0.5, sm: 1 },
      transition: "all 0.2s ease",
    },
    "& .MuiTab-root.Mui-selected": {
      backgroundColor:  darkMode ? "info.light" : "info.dark",
      color: "info.contrastText",
      boxShadow: (paletteTheme) =>
        `0 2px 6px ${alpha(paletteTheme.palette.info.main, 0.35)}`,
    },
    "& .MuiTab-root.Mui-selected .MuiSvgIcon-root": {
      color: "info.contrastText",
    },
    "& .MuiTab-root .MuiSvgIcon-root": {
      marginRight: 0.5,
    },
  };

  const panelContentSx = {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    overflowX: "hidden",
    p: 0.2,
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      width: 0,
      height: 0,
      display: "none",
      background: "transparent",
    },
  };

  return (
    <QueryClientProvider client={queryClient}>
      <MDBox
        sx={{
          //   p: { xs: 1, sm: 1.5 },
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          gap: 0,
          bgcolor: "background.default",
        }}
      >
        <Card
          sx={{
            border: (paletteTheme) =>
              `1px solid ${paletteTheme.palette.divider}`,
            boxShadow: (paletteTheme) =>
              `0 10px 24px ${alpha(paletteTheme.palette.common.black, 0.08)}`,
          }}
        >
          <AppBar
            position="static"
            color="transparent"
            elevation={0}
            sx={{
              // bgcolor: (paletteTheme) =>
              //   alpha(
              //     paletteTheme.palette.info.main,
              //     paletteTheme.palette.mode === "dark" ? 0.12 : 0.05,
              //   ),
              borderBottom: (paletteTheme) =>
                `1px solid ${alpha(paletteTheme.palette.primary.main, 0.18)}`,
            }}
          >
            <Toolbar
              variant="dense"
              sx={{
                // backgroundColor: "info.main",
                minHeight: "58px !important",
                px: { xs: 1, sm: 1.75 },
                py: 0.75,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: { md: 140 },
                }}
              >
                <Chip
                  label={dispatcherTitle}
                  sx={{
                    height: 38,
                    borderRadius: "999px",
                    px: 1.25,
                    color: "info.contrastText",
                    background: (paletteTheme) =>
                      `linear-gradient(135deg, ${paletteTheme.palette.info.main}, ${paletteTheme.palette.info.dark})`,
                    border: (paletteTheme) =>
                      `1px solid ${alpha(paletteTheme.palette.info.main, 0.55)}`,
                    boxShadow: (paletteTheme) =>
                      `0 8px 18px ${alpha(paletteTheme.palette.info.main, 0.3)}`,
                    "& .MuiChip-label": {
                      px: 0.8,
                      fontWeight: 700,
                      fontSize: theme.typography.pxToRem(14),
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: { xs: "stretch", md: "center" },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 1160,
                    display: "flex",
                    gap: 1,
                    flexWrap: { xs: "wrap", md: "nowrap" },
                    alignItems: "center",
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{
                      flex: { xs: "1 1 100%", md: "0 0 230px" },
                      minWidth: { xs: "100%", md: 150 },
                      maxWidth: { xs: "100%", md: 200 },
                    }}
                  >
                    <OutlinedInput
                      value={searchValue}
                      onChange={handleSearchChange}
                      placeholder="Search in active tab"
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchOutlinedIcon fontSize="small" color="info" />
                        </InputAdornment>
                      }
                      endAdornment={
                        searchValue ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchValueState("")}
                              edge="end"
                              sx={{ p: 0.3, color: "error.main" }}
                            >
                              <ClearIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </InputAdornment>
                        ) : null
                      }
                      sx={{
                        borderRadius: 2,
                        bgcolor: (paletteTheme) =>
                          alpha(paletteTheme.palette.background.default, 0.7),
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: (paletteTheme) =>
                            alpha(paletteTheme.palette.info.main, 0.6),
                          borderWidth: 1,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "info.main",
                          borderWidth: 1,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "info.main",
                          borderWidth: 1.25,
                        },
                      }}
                    />
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", md: 200 },
                    }}
                  >
                    <Select
                      value={selectedDepartment}
                      onChange={handleDepartmentChange}
                      displayEmpty
                      inputProps={{ "aria-label": "Select Department" }}
                      style={{ height: "40px" }}
                      renderValue={(val) => {
                        if (!val)
                          return (
                            <span style={{ opacity: 0.6 }}>
                              Select Department
                            </span>
                          );
                        const opt = departmentOptions.find(
                          (o) => o.value === val,
                        );
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            {opt?.color && (
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  bgcolor: opt.color,
                                  border: "1px solid rgba(0,0,0,0.2)",
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <span>{opt?.label || val}</span>
                          </Box>
                        );
                      }}
                      sx={{
                        borderRadius: 2,
                        bgcolor: (paletteTheme) =>
                          alpha(paletteTheme.palette.background.default, 0.7),
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: (paletteTheme) =>
                            alpha(paletteTheme.palette.info.main, 0.6),
                          borderWidth: 1,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "info.main",
                          borderWidth: 1,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "info.main",
                          borderWidth: 1.25,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Department</em>
                      </MenuItem>

                      {departmentOptions.length === 0 && (
                        <MenuItem value="__no_department__" disabled>
                          <em>No Department</em>
                        </MenuItem>
                      )}

                      {departmentOptions.map((department) => (
                        <MenuItem
                          key={department.value}
                          value={department.value}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: department.color || "grey.400",
                                border: "1px solid rgba(0,0,0,0.15)",
                                flexShrink: 0,
                              }}
                            />
                            {department.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box
                    sx={{
                      flex: { xs: "1 1 100%", md: "1 1 auto" },
                      minWidth: { xs: "100%", md: 340 },
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        height: 40,
                        px: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        borderRadius: 2,
                        minWidth: { xs: 0, md: 250 },
                      }}
                    >
                      <FiberManualRecordIcon
                        sx={{ fontSize: 10, color: toolbarConnectionColor }}
                      />
                      <LockOutlinedIcon
                        sx={{ fontSize: 15, color: toolbarConnectionColor }}
                      />
                      {toolbarConnectionLabel && (
                        <MDTypography
                          variant="caption"
                          sx={{
                            color: theme.palette.info.main,
                            fontWeight: 600,
                          }}
                        >
                          {toolbarConnectionLabel}
                        </MDTypography>
                      )}

                      {toolbarCallStatus && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                            px: 0.75,
                            py: 0.25,
                            minWidth: 150,
                            borderRadius: 1,
                          }}
                        >
                          <MDTypography
                            variant="caption"
                            sx={{
                              color: "text",
                              fontWeight: 600,
                            }}
                          >
                            {toolbarCallStatus}
                          </MDTypography>
                        </Box>
                      )}

                      {phoneToolbarState.callActive &&
                        !!activeCallChipLabel && (
                          <Chip
                            size="large"
                            icon={activeCallChipIcon || undefined}
                            label={activeCallChipLabel}
                            sx={{
                              height: 35,
                              maxWidth: { xs: 170, md: 320 },
                              color: `${activeCallChipColorKey}.main`,
                              borderRadius: "999px",
                              border: (paletteTheme) =>
                                `1.2px solid ${alpha(
                                  paletteTheme.palette[activeCallChipColorKey]
                                    ?.main || paletteTheme.palette.info.main,
                                  0.48,
                                )}`,
                              background: (paletteTheme) => {
                                const mainColor =
                                  paletteTheme.palette[activeCallChipColorKey]
                                    ?.main || paletteTheme.palette.info.main;
                                const darkColor =
                                  paletteTheme.palette[activeCallChipColorKey]
                                    ?.dark || mainColor;

                                return `linear-gradient(135deg, ${alpha(mainColor, 0.24)}, ${alpha(darkColor, 0.14)})`;
                              },
                              boxShadow: (paletteTheme) => {
                                const mainColor =
                                  paletteTheme.palette[activeCallChipColorKey]
                                    ?.main || paletteTheme.palette.info.main;

                                return `0 3px 10px ${alpha(mainColor, 0.22)}`;
                              },
                              backdropFilter: "blur(8px)",
                              "& .MuiChip-label": {
                                px: 1,
                                fontWeight: 800,
                                fontSize: "0.72rem",
                                letterSpacing: "0.14px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                              "& .MuiChip-icon": {
                                fontSize: 17,
                                ml: 0.55,
                              },
                            }}
                          />
                        )}

                      {!phoneToolbarState.callActive && !!incomingChipLabel && (
                        <Tooltip title={incomingTypeLabel || "Incoming Call"}>
                          <Chip
                            size="large"
                            icon={incomingChipIcon || undefined}
                            label={incomingChipLabel}
                            sx={{
                              height: 22,
                              maxWidth: { xs: 120, md: 320 },
                              fontWeight: 700,
                              border: (paletteTheme) =>
                                `1px solid ${alpha(
                                  paletteTheme.palette[incomingChipColorKey]
                                    ?.main || paletteTheme.palette.success.main,
                                  0.4,
                                )}`,
                              color: `${incomingChipColorKey}.main`,
                              bgcolor: (paletteTheme) =>
                                alpha(
                                  paletteTheme.palette[incomingChipColorKey]
                                    ?.main || paletteTheme.palette.success.main,
                                  0.12,
                                ),
                              "& .MuiChip-label": {
                                px: 0.9,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                              "& .MuiChip-icon": {
                                fontSize: 16,
                                ml: 0.6,
                              },
                            }}
                          />
                        </Tooltip>
                      )}

                      {showTryingAmbient && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            flexShrink: 0,
                          }}
                        >
                          <Chip
                            size="large"
                            icon={
                              <HearingOutlinedIcon
                                sx={{
                                  fontSize: 16,
                                  mr: 0.25,
                                  color: (theme) =>
                                    `${theme.palette.warning.main} !important`,
                                }}
                              />
                            }
                            label={`Trying Ambient - ${tryingAmbientTarget.label}`}
                            sx={{
                              height: 28,
                              maxWidth: { xs: 160, md: 300 },
                              fontWeight: 700,
                              border: (paletteTheme) =>
                                `1px solid ${alpha(
                                  paletteTheme.palette.warning.main,
                                  0.45,
                                )}`,
                              color: "warning.main",
                              bgcolor: (paletteTheme) =>
                                alpha(paletteTheme.palette.warning.main, 0.14),
                              "& .MuiChip-label": {
                                px: 0.9,
                                fontSize: "0.72rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                              "& .MuiChip-icon": {
                                fontSize: 16,
                                ml: 0.6,
                              },
                            }}
                          />
                          <Tooltip title="Cancel Ambient Request">
                            <IconButton
                              size="small"
                              onClick={cancelTryingAmbient}
                              sx={{
                                border: (paletteTheme) =>
                                  `1px solid ${alpha(
                                    paletteTheme.palette.error.main,
                                    0.32,
                                  )}`,
                                bgcolor: (paletteTheme) =>
                                  alpha(paletteTheme.palette.error.main, 0.12),
                              }}
                            >
                              <CallEndOutlinedIcon
                                fontSize="small"
                                color="error"
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>

                    {showDialButtons && (
                      <OutlinedInput
                        ref={dialPadInputRef}
                        value={toolbarDialValue}
                        onChange={handleToolbarDialChange}
                        placeholder="Dial Number"
                        size="small"
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            triggerToolbarDial(false);
                          }
                        }}
                        endAdornment={
                          <InputAdornment position="end">
                            <Tooltip title="Dial Pad">
                              <IconButton
                                size="small"
                                onClick={handleOpenDialPad}
                                sx={{
                                  color: "info.main",
                                  p: 0.5,
                                }}
                              >
                                <DialpadOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        }
                        sx={{
                          flex: 1,
                          minWidth: { xs: 120, md: 140 },
                          maxWidth: { xs: "100%", md: 200 },
                          height: 40,
                            // bgcolor: (paletteTheme) =>
                            //   alpha(paletteTheme.palette.background.default, 0.7),
                          borderRadius: 2,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: (paletteTheme) =>
                              alpha(paletteTheme.palette.info.main, 0.6),
                            borderWidth: 1,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "info.main",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "info.main",
                            borderWidth: 1.25,
                          },
                        }}
                      />
                    )}

                    <Popover
                      open={Boolean(dialPadAnchorEl) && showDialButtons}
                      anchorEl={dialPadAnchorEl}
                      onClose={handleCloseDialPad}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      disableAutoFocus
                      PaperProps={{
                        elevation: 6,
                        sx: {
                          mt: 0.75,
                          borderRadius: 2,
                          background: darkMode
                            ? "linear-gradient(135deg, #0c0c0c 0%, #060606 50%, #161617 100%)"
                            : "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 50%, #f0f4ff 100%)",
                          border: (paletteTheme) =>
                            `1px solid ${alpha(paletteTheme.palette.info.main, 0.25)}`,
                          p: 1.25,
                          minWidth: 168,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: 0.75,
                        }}
                      >
                        {dialPadKeys.flat().map((key) => (
                          <IconButton
                            key={key}
                            onClick={() => handleDialPadKey(key)}
                            sx={{
                              borderRadius: 1.5,
                              height: 44,
                              width: 44,
                              fontSize: "1rem",
                              fontWeight: 700,
                              border: (paletteTheme) =>
                                `1px solid ${alpha(paletteTheme.palette.info.main, 0.22)}`,
                              color: "text.primary",
                              "&:hover": {
                                // bgcolor: (paletteTheme) =>
                                //   alpha(paletteTheme.palette.info.main, 0.12),
                                borderColor: "info.main",
                                color: "info.main",
                              },
                              transition: "all 0.15s ease",
                            }}
                          >
                            <MDTypography>{key}</MDTypography>
                          </IconButton>
                        ))}
                      </Box>

                      <Box
                        sx={{
                          mt: 0.85,
                          display: "flex",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        <Tooltip title="Audio Call">
                          <span>
                            <IconButton
                              onClick={async () => {
                                handleCloseDialPad();
                                await triggerToolbarDial(false);
                              }}
                              disabled={isToolbarCallActionDisabled}
                              sx={{
                                border: (paletteTheme) =>
                                  `1px solid ${alpha(paletteTheme.palette.success.main, 0.32)}`,
                                bgcolor: (paletteTheme) =>
                                  alpha(
                                    paletteTheme.palette.success.main,
                                    0.12,
                                  ),
                              }}
                            >
                              <CallOutlinedIcon
                                fontSize="small"
                                color="success"
                              />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Video Call">
                          <span>
                            <IconButton
                              onClick={async () => {
                                handleCloseDialPad();
                                await triggerToolbarDial(true);
                              }}
                              disabled={isToolbarCallActionDisabled}
                              sx={{
                                border: (paletteTheme) =>
                                  `1px solid ${alpha(paletteTheme.palette.info.main, 0.32)}`,
                                bgcolor: (paletteTheme) =>
                                  alpha(paletteTheme.palette.info.main, 0.12),
                              }}
                            >
                              <VideocamOutlinedIcon
                                fontSize="small"
                                color="info"
                              />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Clear Number">
                          <span>
                            <IconButton
                              onClick={handleDialPadBackspace}
                              sx={{
                                border: (paletteTheme) =>
                                  `1px solid ${alpha(paletteTheme.palette.error.main, 0.32)}`,
                                bgcolor: (paletteTheme) =>
                                  alpha(paletteTheme.palette.error.main, 0.12),
                              }}
                            >
                              <BackspaceIcon fontSize="small" color="error" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Popover>

                    {showDialButtons && !dialPadAnchorEl && (
                      <Tooltip title="Audio Call">
                        <span>
                          <IconButton
                            onClick={() => triggerToolbarDial(false)}
                            disabled={isToolbarCallActionDisabled}
                            sx={{
                              border: (paletteTheme) =>
                                `1px solid ${alpha(paletteTheme.palette.success.main, 0.32)}`,
                              bgcolor: (paletteTheme) =>
                                alpha(paletteTheme.palette.success.main, 0.12),
                            }}
                          >
                            <CallOutlinedIcon
                              fontSize="small"
                              color="success"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {showDialButtons && !dialPadAnchorEl && (
                      <Tooltip title="Video Call">
                        <span>
                          <IconButton
                            onClick={() => triggerToolbarDial(true)}
                            disabled={isToolbarCallActionDisabled}
                            sx={{
                              border: (paletteTheme) =>
                                `1px solid ${alpha(paletteTheme.palette.info.main, 0.32)}`,
                              bgcolor: (paletteTheme) =>
                                alpha(paletteTheme.palette.info.main, 0.12),
                            }}
                          >
                            <VideocamOutlinedIcon
                              fontSize="small"
                              color="info"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {showClearButton && !dialPadAnchorEl && (
                      <Tooltip title="Clear Number">
                        <span>
                          <IconButton
                            onClick={handleDialPadBackspace}
                            sx={{
                              border: (paletteTheme) =>
                                `1px solid ${alpha(paletteTheme.palette.error.main, 0.32)}`,
                              bgcolor: (paletteTheme) =>
                                alpha(paletteTheme.palette.error.main, 0.12),
                            }}
                          >
                            <BackspaceIcon fontSize="small" color="error" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {showAnswerButton && (
                      <Tooltip title="Answer">
                        <span>
                          <IconButton
                            onClick={triggerToolbarAnswer}
                            disabled={isToolbarAnswerDisabled}
                            sx={{
                              border: (paletteTheme) =>
                                `1px solid ${alpha(paletteTheme.palette.success.main, 0.32)}`,
                              bgcolor: (paletteTheme) =>
                                alpha(paletteTheme.palette.success.main, 0.14),
                            }}
                          >
                            <CallReceivedOutlinedIcon
                              fontSize="small"
                              color="success"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {showRejectButton && (
                      <Tooltip title="Reject">
                        <span>
                          <IconButton
                            onClick={triggerToolbarReject}
                            disabled={isToolbarRejectDisabled}
                            sx={{
                              border: (paletteTheme) =>
                                `1px solid ${alpha(paletteTheme.palette.error.main, 0.32)}`,
                              bgcolor: (paletteTheme) =>
                                alpha(paletteTheme.palette.error.main, 0.14),
                            }}
                          >
                            <CallEndOutlinedIcon
                              fontSize="small"
                              color="error"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {showMicButton && (
                      <Tooltip
                        title={phoneToolbarState.isMuted ? "Mic Off" : "Mic On"}
                      >
                        <span>
                          <IconButton
                            onClick={triggerToolbarMicToggle}
                            sx={{
                              border: (paletteTheme) =>
                                `1px solid ${alpha(
                                  phoneToolbarState.isMuted
                                    ? paletteTheme.palette.error.main
                                    : paletteTheme.palette.success.main,
                                  0.32,
                                )}`,
                              bgcolor: (paletteTheme) =>
                                alpha(
                                  phoneToolbarState.isMuted
                                    ? paletteTheme.palette.error.main
                                    : paletteTheme.palette.success.main,
                                  0.14,
                                ),
                            }}
                          >
                            {phoneToolbarState.isMuted ? (
                              <MicOffIcon fontSize="small" color="error" />
                            ) : (
                              <MicIcon fontSize="small" color="success" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {showVideoToggleButton && (
                      <Tooltip
                        title={
                          phoneToolbarState.isVideoEnabled
                            ? "Video On"
                            : "Video Off"
                        }
                      >
                        <span>
                          <IconButton
                            onClick={triggerToolbarVideoToggle}
                            sx={{
                              border: (paletteTheme) =>
                                `1px solid ${alpha(
                                  phoneToolbarState.isVideoEnabled
                                    ? paletteTheme.palette.info.main
                                    : paletteTheme.palette.grey[600],
                                  0.32,
                                )}`,
                              bgcolor: (paletteTheme) =>
                                alpha(
                                  phoneToolbarState.isVideoEnabled
                                    ? paletteTheme.palette.info.main
                                    : paletteTheme.palette.grey[600],
                                  0.12,
                                ),
                            }}
                          >
                            {phoneToolbarState.isVideoEnabled ? (
                              <VideocamIcon fontSize="small" color="info" />
                            ) : (
                              <VideocamOffIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {phoneToolbarState.callActive && (
                      <Tooltip title={disconnectTooltip}>
                        <span>
                          <IconButton
                            onClick={triggerToolbarDisconnect}
                            disabled={isToolbarDisconnectDisabled}
                            sx={{
                              border: (paletteTheme) =>
                                `1px solid ${alpha(paletteTheme.palette.error.main, 0.32)}`,
                              bgcolor: (paletteTheme) =>
                                alpha(paletteTheme.palette.error.main, 0.14),
                            }}
                          >
                            <CallEndOutlinedIcon
                              fontSize="small"
                              color="error"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "flex-end", md: "flex-end" },
                  gap: 0.5,
                  minWidth: { md: 90 },
                }}
              >
                <Tooltip title={darkMode ? "Light Mode" : "Dark Mode"}>
                  <IconButton
                    onClick={handleToggleDarkMode}
                    sx={{
                      color: "text",
                      border: (paletteTheme) =>
                        `1px solid ${alpha(
                          darkMode
                            ? paletteTheme.palette.warning.main
                            : paletteTheme.palette.info.main,
                          0.25,
                        )}`,
                      bgcolor: (paletteTheme) =>
                        alpha(
                          darkMode
                            ? paletteTheme.palette.warning.main
                            : paletteTheme.palette.info.main,
                          0.08,
                        ),
                      "&:hover": {
                        bgcolor: (paletteTheme) =>
                          alpha(
                            darkMode
                              ? paletteTheme.palette.warning.main
                              : paletteTheme.palette.info.main,
                            0.16,
                          ),
                      },
                    }}
                  >
                    {darkMode ? (
                      <LightModeOutlinedIcon fontSize="small" color="warning" />
                    ) : (
                      <DarkModeOutlinedIcon fontSize="small" color="info" />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Logout">
                  <IconButton
                    onClick={handleLogout}
                    sx={{
                      color: "text",
                      border: (paletteTheme) =>
                        `1px solid ${alpha(paletteTheme.palette.error.main, 0.25)}`,
                      bgcolor: (paletteTheme) =>
                        alpha(paletteTheme.palette.error.main, 0.08),
                      "&:hover": {
                        color: "error",
                        bgcolor: (paletteTheme) =>
                          alpha(paletteTheme.palette.error.main, 0.16),
                      },
                    }}
                  >
                    <LogoutOutlinedIcon fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Toolbar>
          </AppBar>
        </Card>

        {/* Logout Confirmation Dialog */}
        <Dialog
          sx={(theme) => CTLDialogContent(theme, { darkMode })}
          open={logoutDialogOpen}
          onClose={handleLogoutCancel}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            sx={{
              ...CTLDialogContent(theme, { darkMode }),
              display: "flex",
              alignItems: "center",
              gap: 1,
              pb: 0.5,
              fontWeight: 700,
              fontSize: "1.1rem",
              color: "error.main",
            }}
          >
            <LogoutOutlinedIcon fontSize="small" />
            Confirm Logout
          </DialogTitle>
          <DialogContent
            sx={{ ...CTLDialogContent(theme, { darkMode }), pt: 1.5 }}
          >
            <MDTypography variant="body2" color="text" sx={{ mb: 2, mt: 1 }}>
              Please enter your password to verify your identity before logging
              out.
            </MDTypography>
            {logoutNotify.isOpen && (
              <MDTypography
                variant="caption"
                color="error"
                sx={{ display: "block", mb: 1.5, fontWeight: 600 }}
              >
                {logoutNotify.message}
              </MDTypography>
            )}
            <MDInput
              type={showLogoutPassword ? "text" : "password"}
              label="Password"
              fullWidth
              autoFocus
              value={logoutPassword}
              autoComplete="off"
              onChange={(e) => setLogoutPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !logoutLoading) handleLogoutConfirm();
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onMouseDown={() => setShowLogoutPassword(true)}
                      onMouseUp={() => setShowLogoutPassword(false)}
                      onMouseLeave={() => setShowLogoutPassword(false)}
                      size="small"
                    >
                      {showLogoutPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              ...CTLDialogContent(theme, { darkMode }),
            }}
          >
            <MDButton
              variant="gradient"
              color="success"
              size="small"
              onClick={handleLogoutCancel}
              disabled={logoutLoading}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="error"
              size="small"
              onClick={handleLogoutConfirm}
              disabled={logoutLoading}
            >
              {logoutLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Logout"
              )}
            </MDButton>
          </DialogActions>
        </Dialog>
        {/* --- Three-column grid layout with maximize/minimize --- */}
        <Box
          ref={splitContainerRef}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: getGridColumns(),
            gap: maximizedPanel ? 0 : 0,
            transition: "grid-template-columns 0.3s ease",
          }}
        >
          {/* ===== LEFT COLUMN: PPDR CDR / Active Conference (tabs) ===== */}
          <Card
            sx={{
              ...(isPanelVisible("left")
                ? {
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    border: (paletteTheme) =>
                      `1px solid ${paletteTheme.palette.divider}`,
                    overflow: "hidden",
                  }
                : collapsedCardSx),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", pr: 0.5 }}>
              <Tabs
                value={leftPanelTab}
                onChange={(event, value) => setLeftPanelTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ ...panelTabSx, flex: 1 }}
              >
                {LEFT_PANEL_TABS.map((tab) => {
                  const IconComponent = tab.icon;
                  const isSelected = tab.value === leftPanelTab;
                  return (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      label={isSelected ? tab.label : ""}
                      icon={
                        <Tooltip title={tab.label} placement="top">
                          <IconComponent
                            sx={{
                              fontSize: 18,
                              transform: isSelected ? "scale(1)" : "scale(1.4)",
                              transition: "all 0.3s ease",
                            }}
                          />
                        </Tooltip>
                      }
                      iconPosition="start"
                      sx={{
                        backgroundColor: isSelected
                          ? "info.main"
                          : "transparent",
                        color: isSelected
                          ? "info.contrastText"
                          : "text.secondary",
                        transition: "all 0.3s ease",
                      }}
                    />
                  );
                })}
              </Tabs>
              <Tooltip
                title={maximizedPanel === "left" ? "Minimize" : "Maximize"}
              >
                <IconButton
                  size="small"
                  color="text"
                  onClick={() => toggleMaximize("left")}
                  sx={{
                    p: 0.4,
                    color: "text",
                    border: (paletteTheme) =>
                      `1px solid ${alpha(paletteTheme.palette.info.main, 0.32)}`,
                    "&:hover": {
                      color: "info.main",
                      borderColor: "info.main",
                      backgroundColor: (paletteTheme) =>
                        alpha(paletteTheme.palette.info.main, 0.1),
                    },
                  }}
                >
                  {maximizedPanel === "left" ? (
                    <CloseFullscreenOutlinedIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <OpenInFullOutlinedIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>

            <MDBox sx={panelContentSx}>
              {LeftTabComponent && <LeftTabComponent {...sharedPanelProps} />}
            </MDBox>
          </Card>

          {/* ===== CENTER COLUMN: Map / Task Manager / Chat (tabs) ===== */}
          <Card
            sx={{
              ...(isPanelVisible("center")
                ? {
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    border: (paletteTheme) =>
                      `1px solid ${paletteTheme.palette.divider}`,
                    overflow: "hidden",
                  }
                : collapsedCardSx),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", pr: 0.5 }}>
              <Tabs
                value={centerPanelTab}
                onChange={(event, value) => setCenterPanelTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ ...panelTabSx, flex: 1 }}
              >
                {CENTER_PANEL_TABS.map((tab) => {
                  const IconComponent = tab.icon;
                  const isSelected = tab.value === centerPanelTab;
                  const isChatTab = tab.value === "chat";
                  const tabLabel =
                    isChatTab && chatUnreadCount > 0
                      ? `${tab.label} (${chatUnreadCount})`
                      : tab.label;
                  const tabIcon = isChatTab ? (
                    <Badge
                      color="error"
                      badgeContent={chatUnreadCount}
                      max={99}
                      overlap="circular"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.58rem",
                          minWidth: 16,
                          height: 16,
                        },
                      }}
                    >
                      <IconComponent
                        sx={{
                          fontSize: 18,
                          transform: isSelected ? "scale(1)" : "scale(1.4)",
                          transition: "all 0.3s ease",
                        }}
                      />
                    </Badge>
                  ) : (
                    <IconComponent
                      sx={{
                        fontSize: 18,
                        transform: isSelected ? "scale(1)" : "scale(1.4)",
                        transition: "all 0.3s ease",
                      }}
                    />
                  );

                  return (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      label={isSelected ? tabLabel : ""}
                      icon={<Tooltip title={tab.label} placement="top">{tabIcon}</Tooltip>}
                      iconPosition="start"
                      sx={{
                        backgroundColor: isSelected
                          ? "info.main"
                          : "transparent",
                        color: isSelected
                          ? "info.contrastText"
                          : "text.secondary",
                        transition: "all 0.3s ease",
                      }}
                    />
                  );
                })}
              </Tabs>
              <Tooltip
                title={maximizedPanel === "center" ? "Minimize" : "Maximize"}
              >
                <IconButton
                  size="small"
                  color="text"
                  onClick={() => toggleMaximize("center")}
                  sx={{
                    p: 0.4,
                    color: "text",
                    border: (paletteTheme) =>
                      `1px solid ${alpha(paletteTheme.palette.info.main, 0.32)}`,
                    "&:hover": {
                      color: "info.main",
                      borderColor: "info.main",
                      backgroundColor: (paletteTheme) =>
                        alpha(paletteTheme.palette.info.main, 0.1),
                    },
                  }}
                >
                  {maximizedPanel === "center" ? (
                    <CloseFullscreenOutlinedIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <OpenInFullOutlinedIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>

            <MDBox sx={panelContentSx}>
              {CenterTabComponent && (
                <CenterTabComponent {...sharedPanelProps} />
              )}
            </MDBox>
          </Card>

          {/* ===== RIGHT COLUMN: Alarm View / Alert View / Waiting Call (sub-tabs) ===== */}
          <Card
            sx={{
              ...(isPanelVisible("right")
                ? {
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    border: (paletteTheme) =>
                      `1px solid ${paletteTheme.palette.divider}`,
                    overflow: "hidden",
                  }
                : collapsedCardSx),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", pr: 0.5 }}>
              <Tabs
                value={rightPanelTab}
                onChange={handleRightPanelTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ ...panelTabSx, flex: 1 }}
              >
                {RIGHT_PANEL_TABS.map((tab) => {
                  const IconComponent = tab.icon;
                  const isSelected = tab.value === rightPanelTab;
                  const isWaiting = tab.value === "waiting-call";
                  const waitingCount = effectiveWaitingCallsCount;
                  const dispatcherBadgeCount =
                    tab.value === "alarm-view"
                      ? dispatcherAlertCounts.sos
                      : tab.value === "active-calls"
                        ? dispatcherAlertCounts.activeCalls
                        : 0;
                  const tabLabel =
                    isWaiting && waitingCount > 0
                      ? `${tab.label} (${waitingCount})`
                      : tab.label;

                  return (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      label={isSelected ? tabLabel : ""}
                      icon={
                        <Tooltip title={tab.label} placement="top">
                          <Badge
                            badgeContent={dispatcherBadgeCount}
                            color="error"
                            invisible={isSelected || dispatcherBadgeCount <= 0}
                            overlap="circular"
                            sx={{
                              "& .MuiBadge-badge": {
                                backgroundColor: "error.main",
                                color: "common.white",
                                fontWeight: 700,
                              },
                            }}
                          >
                            <IconComponent
                              sx={{
                                fontSize: 18,
                                transform: isSelected ? "scale(1)" : "scale(1.4)",
                                transition: "all 0.3s ease",
                              }}
                            />
                          </Badge>
                        </Tooltip>
                      }
                      iconPosition="start"
                      sx={{
                        backgroundColor: isSelected
                          ? "info.main"
                          : "transparent",
                        color: isSelected
                          ? "info.contrastText"
                          : "text.secondary",
                        transition: "all 0.3s ease",
                      }}
                    />
                  );
                })}
              </Tabs>
              <Tooltip
                title={
                  isRightMaximizeDisabled
                    ? "Disabled during video call"
                    : maximizedPanel === "right"
                      ? "Minimize"
                      : "Maximize"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="text"
                    disabled={isRightMaximizeDisabled}
                    onClick={() => toggleMaximize("right")}
                    sx={{
                      p: 0.4,
                      color: "text",
                      border: (paletteTheme) =>
                        `1px solid ${alpha(paletteTheme.palette.info.main, 0.32)}`,
                      "&:hover": {
                        color: "info.main",
                        borderColor: "info.main",
                        backgroundColor: (paletteTheme) =>
                          alpha(paletteTheme.palette.info.main, 0.1),
                      },
                    }}
                  >
                    {maximizedPanel === "right" ? (
                      <CloseFullscreenOutlinedIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <OpenInFullOutlinedIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <MDBox sx={{ ...panelContentSx, display: "flex", flexDirection: "column", gap: 0.75 }}>
             {rightPanelTab === "alarm-view" &&
                (phoneToolbarState.remoteStreams || []).some(
                  (s) => s?.stream && s?.hasVideo
                ) &&
                !isPrivateAudioCallActive && (
                <>
                  <VideoStreamArea
                    remoteStreams={phoneToolbarState.remoteStreams || []}
                    callStreamId={phoneToolbarState.callStreamId || ""}
                    darkMode={darkMode}
                  />
                  <AudioStreamArea
                    remoteStreams={phoneToolbarState.remoteStreams || []}
                    videoPanelVisible
                  />
                </>
              )}
              {RightTabComponent && (
                <RightTabComponent {...sharedPanelProps} />
              )}
            </MDBox>
          </Card>
        </Box>

        {/* Hidden WebRTCPhone for phone functionality */}
        <Box
          sx={{
            position: "fixed",
            ...(showAnnouncementPanel
              ? {
                  display: "flex",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "92vw", sm: 420 },
                  maxHeight: "90vh",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1300,
                }
              : {
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                  opacity: 0,
                  pointerEvents: "none",
                  zIndex: -1,
                }),
          }}
        >
          <WebRTCPhone
            ref={phoneRef}
            compactMode
            allowVideoPopup={false}
            onStatusChange={handlePhoneStatusChange}
            contacts={contacts}
          />
        </Box>
        <CTLNotification notify={chatNotify} setNotify={setChatNotify} />
        <CTLNotification notify={contactNotify} setNotify={setContactNotify} />
      </MDBox>
    </QueryClientProvider>
  );
}
